import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Table, Modal, Spin, Empty, Tag, Space, Dropdown } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/en";
import {
  listScorecards,
  createScorecard,
  deleteScorecard,
} from "./balancedScorecard.service";
import type { ApiScorecardSummary } from "./balancedScorecard.service";
import "./BalancedScorecardPage.css";

dayjs.extend(relativeTime);
dayjs.locale("en");

interface ScorecardListState {
  scorecards: ApiScorecardSummary[];
  loading: boolean;
  error: string | null;
  total: number;
}

const BalancedScorecardListPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("balancedScorecard");

  const [state, setState] = useState<ScorecardListState>({
    scorecards: [],
    loading: true,
    error: null,
    total: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [deleteModal, setDeleteModal] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadScorecards();
    }
  }, [projectId]);

  const loadScorecards = async (page: number = 1) => {
    if (!projectId) return;
    setCurrentPage(page);
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const skip = (page - 1) * pageSize;
      const result = await listScorecards(projectId, skip, pageSize);
      setState((prev) => ({
        ...prev,
        scorecards: result.scorecards,
        total: result.total,
        loading: false,
      }));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load scorecards";
      setState((prev) => ({ ...prev, error: message, loading: false }));
      toast.error(message);
    }
  };

  const handleAddNew = () => {
    if (!projectId) return;
    navigate(`/balanced-scorecard/${projectId}/new`);
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await deleteScorecard(deleteModal);
      toast.success(t("messages.deleted"));
      await loadScorecards();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete scorecard";
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteModal(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published":
        return "green";
      case "draft":
        return "orange";
      case "outdated":
        return "red";
      default:
        return "default";
    }
  };

  const getTrafficLightColor = (light: string | null) => {
    if (!light) return "#6b7280";
    switch (light) {
      case "green":
        return "#16a34a";
      case "yellow":
        return "#d97706";
      case "red":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  const getTrafficLightBg = (light: string | null) => {
    if (!light) return "#f3f4f6";
    switch (light) {
      case "green":
        return "#dcfce7";
      case "yellow":
        return "#fef3c7";
      case "red":
        return "#fee2e2";
      default:
        return "#f3f4f6";
    }
  };

  const columns = [
    {
      title: t("labels.title"),
      dataIndex: "title",
      key: "title",
      render: (text: string, record: ApiScorecardSummary) => (
        <a
          onClick={() =>
            navigate(`/balanced-scorecard/${projectId}/${record.id}`)
          }
        >
          {text}
        </a>
      ),
    },
    {
      title: t("labels.status"),
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: t("labels.score"),
      dataIndex: "overall_score",
      key: "overall_score",
      width: 100,
      align: "center" as const,
      render: (score: number | null, record: ApiScorecardSummary) => {
        if (score === null || score === undefined) {
          return <span style={{ color: "#9ca3af" }}>-</span>;
        }
        return (
          <div
            style={{
              padding: "4px 8px",
              borderRadius: "4px",
              textAlign: "center",
              fontWeight: "bold",
              color: getTrafficLightColor(
                record?.overall_traffic_light ?? null,
              ),
              backgroundColor: getTrafficLightBg(
                record?.overall_traffic_light ?? null,
              ),
            }}
          >
            {Math.round(score)}%
          </div>
        );
      },
    },
    {
      title: t("labels.indicators"),
      key: "indicators",
      width: 150,
      render: (_: unknown, record: ApiScorecardSummary) => (
        <span style={{ fontSize: "12px", color: "#6b7280" }}>
          {record.indicator_count} indicators • {record.domain_count} domains
        </span>
      ),
    },
    {
      title: t("labels.published"),
      dataIndex: "published_at",
      key: "published_at",
      width: 150,
      render: (published_at: string | null) =>
        published_at ? (
          <span style={{ color: "#6b7280", fontSize: "12px" }}>
            {dayjs(published_at).fromNow()}
          </span>
        ) : (
          <span style={{ color: "#d1d5db" }}>-</span>
        ),
    },
    {
      title: t("labels.updated"),
      dataIndex: "updated_at",
      key: "updated_at",
      width: 150,
      render: (updated_at: string) => (
        <span style={{ color: "#6b7280", fontSize: "12px" }}>
          {dayjs(updated_at).fromNow()}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: unknown, record: ApiScorecardSummary) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: t("buttons.delete"),
                danger: true,
                onClick: () => setDeleteModal(record.id),
              },
            ],
          }}
        >
          <Button type="text" size="small" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  if (state.loading) {
    return (
      <div className="bsc-page">
        <div className="bsc-card" style={{ padding: 60, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="bsc-page">
      <div className="bsc-card">
        <div className="bsc-list-header">
          <h1 className="bsc-list-title">{t("labels.balancedScorecards")}</h1>
          <Button
            className="bsc-add-new-btn"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            size="large"
          >
            {t("buttons.addNew")}
          </Button>
        </div>

        {/* <div className="bsc-list-table"> */}
        <Table
          className="bsc-data-table"
          columns={columns}
          dataSource={state.scorecards}
          rowKey="id"
          loading={state.loading}
          pagination={{
            current: currentPage,
            pageSize,
            total: state.total,
            onChange: (page) => loadScorecards(page),
            showTotal: (total) =>
              t("pagination.total", { count: total }) || `Total: ${total}`,
          }}
          size="middle"
          scroll={{ x: 1200 }}
          locale={{
            emptyText: t("messages.noScorecardsFound"),
          }}
        />
        {/* </div> */}
      </div>

      <Modal
        open={!!deleteModal}
        onCancel={() => setDeleteModal(null)}
        footer={null}
        width={400}
        centered
        destroyOnClose
      >
        <div className="bsc-delete-modal">
          <div className="bsc-delete-icon">
            <ExclamationCircleOutlined />
          </div>
          <h3 className="bsc-delete-title">{t("modals.deleteScorecard")}</h3>
          <p className="bsc-delete-text">
            {t("messages.deleteScorecardWarning")}
          </p>
          <div className="bsc-delete-actions">
            <button
              className="bsc-btn-cancel"
              onClick={() => setDeleteModal(null)}
              disabled={deleting}
            >
              {t("buttons.cancel")}
            </button>
            <button
              className="bsc-btn-delete"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? <Spin size="small" /> : t("buttons.delete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BalancedScorecardListPage;
